import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_ModelDataSourceRow,
} from "../PA.Core/PAdbSchemaRows";
import {IwbModelDataSource} from "../PA.Core/PAdbSchemaRowsIF";
import {
     esWB_Model_DataSource,
    esWB_Simulation_Predictor_Relationship
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {PAEntity} from "../PA.Core/PAEntity";

export class ModelDataSourceBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ModelDataSource: WB_ModelDataSourceRow;

    _bIsIdEditable: boolean;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New ModelData Source'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_ModelDataSource = null;
        this._bIsIdEditable = true
    }

    get WB_ModelDataSource(): WB_ModelDataSourceRow {
        return this.drWB_ModelDataSource;
    }

    get bIsIdEditable(): boolean {
        return this._bIsIdEditable
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating ModelData Source");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for new ModelData Source.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Model_DataSource)
                .then((newModelDataSourceRow: WB_ModelDataSourceRow) => {
                    newModelDataSourceRow.setDefaults();
                    self.drWB_ModelDataSource = newModelDataSourceRow;
                    self.dbContext.add(esWB_Model_DataSource, newModelDataSourceRow);
                    self.Identity.setAll(self.WB_ModelDataSource.cif.WB_MODEL_DATASOURCE_ID, self.WB_ModelDataSource.cif.DATASOURCE_ID, self.WB_ModelDataSource.cif.DATASOURCE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ModelDataSource, 'WB_MODEL_DATASOURCE_ID', 'DATASOURCE_ID', 'DATASOURCE_NAME');
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._bIsIdEditable = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err)
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                let sError = "Model Data Source already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Model Data Source.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = ModelDataSourceBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = ModelDataSourceBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let newModelDataSourceRow: any = recordResolver.get(esWB_Model_DataSource, anId);

                    if (_.isEmpty(newModelDataSourceRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_ModelDataSource = newModelDataSourceRow;
                    self.Identity.setAll(self.WB_ModelDataSource.cif.WB_MODEL_DATASOURCE_ID, self.WB_ModelDataSource.cif.DATASOURCE_ID, self.WB_ModelDataSource.cif.DATASOURCE_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_ModelDataSource, 'WB_MODEL_DATASOURCE_ID', 'DATASOURCE_ID', 'DATASOURCE_NAME');
                })
                .then((newModelDataSourceRow: any) => {
                    let aPAEntity = new PAEntity()
                    return aPAEntity.checkCustomColumnExists("ModelPredictor", self.drWB_ModelDataSource.cif.WB_MODEL_DATASOURCE_ID, "/AZR/WB_SIMULATION_PREDICTORR", self.drWB_ModelDataSource.cif.DATASOURCE_ID)
                }).then((bExist: boolean) => {
                self._bIsIdEditable = !bExist
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                resolve(self);
            })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {

        const self = this;

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_ModelDataSource.cif.DATASOURCE_NAME + "(" + self.WB_ModelDataSource.cif.DATASOURCE_ID + ")");

        // Does no validations yet
        self.ValidateOptions = options || {};
        try {
            if (self.dbContext == undefined) {
                let sError = "Unable to determine dbContext to Validate Model Data Source.";
                self.ValidateProgressProvider.exception(sError);
                self._bIsValidated = false
                return self;
            }

            // await ModelPredictorBOValidations.ValidateFields(self)

            self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0;
            self.ValidateProgressProvider.finish();
        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
            return self;
        }

        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        let aPAEntity = new PAEntity();
        const wbModelDataSource = <IwbModelDataSource>self.drWB_ModelDataSource;

        self.SaveProgressProvider.start("Saving " + wbModelDataSource.DATASOURCE_NAME + "(" + wbModelDataSource.DATASOURCE_ID + ")");
        self._bIsSaved = false;
        self.SaveOptions = options || {};

        try {
            if (self.dbContext == null) {
                let sError = "Unable to determine dbContext to Save Model Data Source.";
                self.SaveProgressProvider.exception(sError);
                return self
            }


            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            let tasks: Promise<any> [] = [];

            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            await saveFunction(esWB_Model_DataSource, self.drWB_ModelDataSource, {})

            await aPAEntity.createOrUpdateCustomColumn(esWB_Model_DataSource,
                                                        self.drWB_ModelDataSource.cif.WB_MODEL_DATASOURCE_ID,
                                                        "/AZR/WB_SIMULATION_PREDICTORR",
                                                        self.drWB_ModelDataSource.cif.DATASOURCE_ID,
                                                        {
                                                            APP_ID: self.drWB_ModelDataSource.cif.DATASOURCE_ID,
                                                            MAPPER_ID: esWB_Simulation_Predictor_Relationship,
                                                            SERVER_MODEL_ID: esWB_Simulation_Predictor_Relationship,
                                                            COLUMN_DATATYPE: "DECIMAL",
                                                            COLUMN_LENGTH: 15,
                                                            COLUMN_PRECISION: 6,
                                                            // NOTES: self.drWB_ModelDataSource.cif.COMMENTS || ''
                                                        })


            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }

        return self;

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbModelDataSource = <IwbModelDataSource>self.drWB_ModelDataSource;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbModelDataSource.DATASOURCE_NAME + "(" + wbModelDataSource.DATASOURCE_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Model Predictor

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ModelDataSource.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const modelDataSource_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, modelDataSource_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Model_DataSource, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(ModelDataSourceBO, "ModelDataSourceBO", "Model DataSource")